<template>
  <q-drawer v-model="drawer" :breakpoint="500" overlay bordered side="left">
    <q-scroll-area class="fit">
      <div class="bg-primary text-white q-pa-md">
        <div class="row items-center justify-between">
          <div class="text-h6 text-weight-bold">Menu</div>
          <q-btn
            flat
            round
            dense
            :icon="mdiClose"
            @click="closeDrawer"
            class="text-white"
          />
        </div>
      </div>

      <q-list>
        <q-item-label header class="text-weight-bold text-primary">
          Categories
        </q-item-label>

        <div
          v-if="categoryStore.categories.length === 0"
          class="text-center text-dark"
        >
          <q-spinner-dots color="primary" size="20px" />
        </div>

        <q-tree
          v-else
          :nodes="treeNodes"
          node-key="_id"
          v-model:expanded="expandedNodes"
          v-model:selected="selectedNode"
          @update:expanded="val => (expandedNodes = val as string[])"
          @update:selected="onNodeSelected"
          selected-color="primary"
          color="primary"
          :icon="mdiFolder"
          selectable
          clickable
        />
      </q-list>
    </q-scroll-area>
  </q-drawer>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useCategoryStore } from "@/stores/categoryStore";
import { useAppStore } from "@/stores/appStore";
import { useRouter } from "vue-router";
import { NAIL_SUPPLY_ROUTE_NAME } from "@/libs/route-name";
import { mdiClose, mdiFolder, mdiChevronRight } from "@quasar/extras/mdi-v7";

// Props
interface Props {
  modelValue: boolean;
}

const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

// Stores and router
const appStore = useAppStore();
const categoryStore = useCategoryStore();
const router = useRouter();

// Computed
const drawer = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value)
});

const parentCategories = computed(() => {
  return categoryStore?.categories?.filter(cat => cat.parent_id === null);
});

const treeNodes = computed(() => {
  return buildCategoryTree(parentCategories.value);
});

const expandedNodes = ref<string[]>([]);
const selectedNode = ref<string | null>(null);

const buildCategoryTree = (categories: any[]): any[] => {
  return categories.map(category => {
    const children = getChildCategories(category._id);
    const hasChildren = children.length > 0;

    return {
      _id: category._id,
      label: `${category.name}${category.description ? ` • ${category.description}` : ""}`,
      description: category.description,
      icon: mdiChevronRight,
      children: hasChildren ? buildCategoryTree(children) : [],
      isLeaf: !hasChildren
    };
  });
};

const getChildCategories = (parentId: string) => {
  return categoryStore?.categories?.filter(cat => cat.parent_id === parentId);
};

const onNodeSelected = (nodeId: string) => {
  if (!nodeId) return;

  const allCategories = [
    ...parentCategories.value,
    ...categoryStore?.categories?.filter(cat => cat.parent_id !== null)
  ];
  const selectedCategory = allCategories.find(cat => cat._id === nodeId);

  if (selectedCategory) {
    const hasSubcategories =
      getChildCategories(selectedCategory._id).length > 0;

    if (hasSubcategories) {
      // Toggle expanded state for categories with children
      const isExpanded = expandedNodes.value.includes(nodeId);
      if (isExpanded) {
        expandedNodes.value = expandedNodes.value.filter(id => id !== nodeId);
      } else {
        expandedNodes.value.push(nodeId);
      }
      selectedNode.value = null;
    } else {
      // Navigate to leaf category (no children)
      appStore.setDrawerOpen(false);
      navigateToCategory(selectedCategory);
    }
  }
};

const navigateToCategory = (category: any) => {
  router.push({
    name: NAIL_SUPPLY_ROUTE_NAME.PRODUCT,
    params: { category: category.slug },
    query: { type: category.type, catId: category._id }
  });
};

const closeDrawer = () => {
  drawer.value = false;
};
</script>

<style scoped>
:deep(.q-item) {
  border-radius: 4px;
  margin: 2px 8px;
}
</style>
